import { useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { FullPageLoader, PageContentLayout } from '@tassomutuo-backoffice/ui';

import DashboardContent from './components/DashboardFatturatoPersoContent';
import LeadPersaCardList from './components/LeadPersaCardList';
import useDashboardPdf from './hooks/useDashboardPdf';
import DashboardHeaderSection from './components/DashboardHeaderSection';
import useStatistics from './hooks/useStatistics';
import formatMoney from '../../utils/formatMoney';
import StatisticLabelValueItem from './components/StatisticLabelValueItem';
import { convertToCSV, downloadCSV } from '../../utils/csvExport';
import useDateFormat from '../../hooks/useDateFormat';

const DashboardFatturatoPersoPage = () => {
  const { isPrinting, generatePdf } = useDashboardPdf({
    from: 'fatturato-perso',
  });
  const contentPrintableRef = useRef<HTMLDivElement>(null);
  const { formatDate } = useDateFormat();

  const {
    isPending,
    error,
    isTeamStatistics,
    selectedPeriod,
    setSelectedPeriod,
    selectedMember,
    setSelectedMember,
    teamGroups,
    memberSelectOptions,
    selectedGroupId,
    setSelectedGroupId,
    statisticsToShow,
    benchmarkStatistics,
    dateRange,
    setDateRange,
    lostRevenueLeads,
    getFilterText,
  } = useStatistics({ from: 'fatturato-perso' });

  // Funzione per esportare le lead perse in CSV
  const handleExportCsv = useCallback(() => {
    if (!lostRevenueLeads || lostRevenueLeads.length === 0) return;

    // Trova il nome del consulente dal memberSelectOptions
    const getMemberName = (hubspotOwnerId: string) => {
      const member = memberSelectOptions?.find(
        (m) => m.hubspotOwnerId === hubspotOwnerId
      );
      return member?.name || member?.email || hubspotOwnerId || '';
    };

    // Helper per formattare le date (gestisce oggetti { value: string } o stringhe)
    const safeFormatDate = (dateValue: unknown, format = 'dd/MM/yyyy') => {
      if (!dateValue) return '';
      try {
        const dateStr = typeof dateValue === 'object' && dateValue !== null && 'value' in dateValue
          ? (dateValue as { value: string }).value
          : dateValue as string;
        if (!dateStr) return '';
        return formatDate(dateStr, format);
      } catch {
        return '';
      }
    };

    // Formatta i dati con header leggibili in italiano
    const csvData = lostRevenueLeads.map((lead) => ({
      'Nome Lead': lead.dealname || '',
      'Telefono': lead.phone_number || '',
      'Fatturato Perso': lead.amount || '',
      'Valore Immobile': lead.property_value || '',
      'Importo Mutuo': lead.loan_amount || '',
      'Data Creazione': safeFormatDate(lead.createdate, 'dd/MM/yyyy HH:mm'),
      'Ultima Attività': safeFormatDate(lead.notes_last_updated, 'dd/MM/yyyy HH:mm'),
      'Data Operazione Immobiliare': safeFormatDate(lead.data_operazione_immobiliare),
      'Motivo Perdita': lead.why_dropped ? lead.why_dropped.split('.').pop() || '' : '',
      'Consulente': getMemberName(lead.hubspot_owner_id),
    }));

    const csvContent = convertToCSV(csvData);
    const filename = `fatturato-perso_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  }, [lostRevenueLeads, memberSelectOptions, formatDate]);

  return (
    <Box sx={{ backgroundColor: '#E9EAEB', minHeight: '100vh' }}>
      <FullPageLoader show={isPending || isPrinting} />

      <PageContentLayout error={error} containerSx={{ pt: 0 }}>
        <DashboardHeaderSection
          title="Fatturato perso"
          subtitle={
            isTeamStatistics
              ? 'Monitora i potenziali mutui persi dei tuoi consulenti'
              : 'Monitora i potenziali mutui persi'
          }
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          onExportPdfClick={() =>
            generatePdf('download', contentPrintableRef, getFilterText())
          }
          onExportCsvClick={handleExportCsv}
          selectedMember={selectedMember}
          setSelectedMember={setSelectedMember}
          teamGroups={teamGroups || []}
          memberSelectOptions={memberSelectOptions}
          selectedGroupId={selectedGroupId}
          setSelectedGroupId={setSelectedGroupId}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
        {/* This div is what html2canvas will capture */}
        {!selectedMember ||
        (selectedMember && !!selectedMember.hubspotOwnerId) ? (
          <Box
            ref={contentPrintableRef}
            sx={{ px: 0, pb: 4, width: isPrinting ? 1100 : undefined }}
          >
            <StatisticLabelValueItem
              label="Fatturato perso"
              value={formatMoney(
                statisticsToShow?.statisticsAllPeriod?.lostRevenue || 0
              )}
              // trendValue={
              //   statisticsToShow?.statisticsAllPeriodTrend?.lostRevenue
              // }
              // isSelected={selectedCard === 'lostRevenue'}
              // onClick={() => onChangeCard('lostRevenue')}
              valueColor="rgba(239, 68, 68, 1)"
              reverseTrend
            />

            {lostRevenueLeads.map((lead, index) => (
              <Box
                data-page-break={
                  (index + 1) % 5 === 0 || index === lostRevenueLeads.length - 1 ? 'after' : undefined
                }
              >
                <LeadPersaCardList
                  key={lead.hs_object_id}
                  lead={lead}
                  members={memberSelectOptions || []}
                  isPrinting={isPrinting}
                />
              </Box>
            ))}

            <DashboardContent
              isTeamStatistics={isTeamStatistics}
              funnelPerStageStatistics={
                statisticsToShow?.funnelPerStageStatistics
              }
              statisticsAllPeriod={statisticsToShow?.statisticsAllPeriod}
              statisticsAllPeriodTrend={
                statisticsToShow?.statisticsAllPeriodTrend
              }
              statisticsPerMonth={statisticsToShow?.statisticsPerMonth}
              benchmarkStatistics={benchmarkStatistics}
              showAllCharts={isPrinting}
              isPrinting={isPrinting}
              droppedReasons={statisticsToShow?.droppedReasons}
            />
          </Box>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" px={4} pt={1}>
              Non ci sono dati da mostrare per il membro selezionato
            </Typography>
          </Box>
        )}
      </PageContentLayout>
    </Box>
  );
};

export default DashboardFatturatoPersoPage;
